import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { motion } from 'framer-motion';
import { emrService, EmrClinicalNote } from '@/services/emrService';
import { useClinic } from '@/contexts/ClinicContext';
import { toast } from '@/hooks/use-toast';

interface SOAPNotesListSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
}

const SOAPNotesListSheet = ({ open, patientId, onOpenChange }: SOAPNotesListSheetProps) => {
  const { clinic } = useClinic();
  const [notes, setNotes] = useState<EmrClinicalNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const fetchNotes = async (page: number) => {
    if (!clinic?.id) return;
    setIsLoading(true);
    try {
      const response = await emrService.getPatientNotesPaginated(clinic.id, patientId, {
        page,
        limit,
      });
      setNotes(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
      setCurrentPage(page);
    } catch (error: any) {
      console.error('Error fetching clinical notes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load clinical notes',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && patientId && clinic?.id) {
      fetchNotes(1);
    }
  }, [open, patientId, clinic?.id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPractitionerName = (note: EmrClinicalNote) => {
    if (note.practitioner) {
      return `Dr. ${note.practitioner.first_name} ${note.practitioner.last_name}`;
    }
    return 'Unknown Practitioner';
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      fetchNotes(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchNotes(currentPage + 1);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[800px] sm:max-w-[800px] overflow-y-auto p-6">
        <SheetHeader>
          <SheetTitle>All Clinical Notes ({total})</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No clinical notes found
            </div>
          ) : (
            <>
              {notes.map((note) => (
                <motion.div
                  key={note.id}
                  className="p-4 border border-slate-200 rounded-lg bg-white"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-slate-900">{getPractitionerName(note)}</div>
                        {note.is_signed && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                            <Lock className="h-3 w-3" />
                            Signed
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {formatDate(note.note_date || note.created_at)}
                        {note.note_type !== 'soap' && (
                          <span className="ml-2 text-slate-400">· {note.note_type}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {note.subjective && (
                      <div className="text-sm">
                        <div className="font-semibold text-slate-700 mb-1">Subjective:</div>
                        <div className="text-slate-600 whitespace-pre-wrap">{note.subjective}</div>
                      </div>
                    )}

                    {note.objective && (
                      <div className="text-sm">
                        <div className="font-semibold text-slate-700 mb-1">Objective:</div>
                        <div className="text-slate-600 whitespace-pre-wrap">{note.objective}</div>
                      </div>
                    )}

                    {note.assessment && (
                      <div className="text-sm">
                        <div className="font-semibold text-slate-700 mb-1">Assessment:</div>
                        <div className="text-slate-600 whitespace-pre-wrap">{note.assessment}</div>
                      </div>
                    )}

                    {note.plan && (
                      <div className="text-sm">
                        <div className="font-semibold text-slate-700 mb-1">Plan:</div>
                        <div className="text-slate-600 whitespace-pre-wrap">{note.plan}</div>
                      </div>
                    )}

                    {note.content && !note.subjective && !note.objective && !note.assessment && !note.plan && (
                      <div className="text-sm">
                        <div className="text-slate-600 whitespace-pre-wrap">{note.content}</div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-slate-500">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1 || isLoading}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages || isLoading}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SOAPNotesListSheet;
